import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { sendAnalyticsInfo } from 'features/analytics/helpers';
import type { RootState } from 'features/app/store/store';
import { callIdSelector } from 'features/call/call-base/store/selectors';
import { VoiceMeetingService } from 'features/voice-meeting/services';
import { VoiceSessionStatus } from 'features/voice-session/enums';
import type { VoiceSessionState } from 'features/voice-session/interfaces';

export const initialVoiceSessionState: VoiceSessionState = {
  sessionId: '',
  status: VoiceSessionStatus.DISCONNECTED,
  isLoading: false,
};

export const voiceSessionSlice = createSlice({
  name: 'voiceSession',
  initialState: initialVoiceSessionState,
  reducers: {
    setVoiceSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },
    setVoiceSession: (state, action: PayloadAction<VoiceSessionState>) => {
      return action.payload;
    },
    setVoiceSessionStatus: (
      state,
      action: PayloadAction<VoiceSessionStatus>
    ) => {
      state.status = action.payload;
    },
    setVoiceSessionLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    resetVoiceSession: () => initialVoiceSessionState,
  },
});

export const {
  setVoiceSession,
  setVoiceSessionId,
  setVoiceSessionStatus,
  setVoiceSessionLoading,
  resetVoiceSession,
} = voiceSessionSlice.actions;

export const voiceSessionReducer = voiceSessionSlice.reducer;

export const voiceSessionIdSelector = ({ voiceSession }: RootState) =>
  voiceSession.sessionId;

export const voiceSessionStatusSelector = ({ voiceSession }: RootState) =>
  voiceSession.status;

export const isVoiceSessionLoadingSelector = ({ voiceSession }: RootState) =>
  voiceSession.isLoading;

export const isVoiceSessionActiveSelector = ({ voiceSession }: RootState) =>
  voiceSession.status === VoiceSessionStatus.ACTIVE;

export const setVoiceSessionThunk = createAsyncThunk(
  'voiceSession/setVoiceSessionThunk',
  async (
    { voiceSessionState }: { voiceSessionState: VoiceSessionState },
    { dispatch, getState }
  ) => {
    const callId = callIdSelector(getState() as RootState);
    const service = VoiceMeetingService.getInstance();
    if (callId === 0) {
      service.resetService();
      dispatch(
        sendAnalyticsInfo({
          Message:
            'Not setting voice session state when call ID is 0 - resetting service',
        })
      );
    } else {
      dispatch(setVoiceSession(voiceSessionState));
      return voiceSessionState;
    }
    return null;
  }
);
